import type { LoggerConfig } from '../types/LoggerConfig';

import generateUid from './generate-uid';

type Global = {
    chaynsLoggerConfig: LoggerConfig,
    chaynsLoggerConfigs: LoggerConfig[],
};

/**
 * Default config
 * @type {LoggerConfig}
 * @private
 */
// use own variable for configs because electron has no window
let electron = {};
let myWindow: Global = (typeof window !=='undefined' && window || electron) as Global;

myWindow.chaynsLoggerConfig = {
    sessionUid: generateUid(),
    applicationUid: '',
    throttleTime: null,
    maxBatchSize: 5000,
    defaults: {},
    fetch: null,
    localStorage: null,
};

let currentApplicationUid: string;

myWindow.chaynsLoggerConfigs = myWindow.chaynsLoggerConfigs || [];
/**
 * Merges default config and provided config object, overwriting the defaults.
 * @param config
 */
export function configure(config: LoggerConfig): void {
    if (!currentApplicationUid) {
        currentApplicationUid = config.applicationUid;
    }

    const _config = myWindow.chaynsLoggerConfig;
    myWindow.chaynsLoggerConfigs.push({
        ..._config,
        ...config
    });
}

export function get<T extends keyof LoggerConfig>(key: T, applicationUid?: string | null): LoggerConfig[T] | undefined;
export function get<T extends keyof LoggerConfig>(key: undefined, applicationUid?: string | null): LoggerConfig  | undefined;

/**
 * Retrieves either a config entry or the entire config, if no key is provided.
 * @param key
 * @param applicationUid
 * @returns {{any}}
 */
export function get<T extends keyof LoggerConfig>(key: T | undefined, applicationUid: string | null | undefined = null): LoggerConfig[T] | LoggerConfig | undefined {
    const _configs = myWindow.chaynsLoggerConfigs;
    if (applicationUid !== null) {
        const applicationIndex = _configs.findIndex((conf) => conf.applicationUid === applicationUid);
        if (applicationIndex > -1) {
            return key ? _configs[applicationIndex][key] : _configs[applicationIndex];
        }
    }
    const findConfigIndex = _configs.findIndex((conf) => conf.applicationUid === currentApplicationUid);
    const index = findConfigIndex > - 1 ? findConfigIndex : 0;
    return key ? _configs[index][key] : _configs[index];
}

/**
 * Sets an item in the config.
 * @param key
 * @param value
 * @param applicationUid
 */
export function set<T extends keyof LoggerConfig>(key: T, value: LoggerConfig[T], applicationUid: string | null = null): void {
    const _configs = myWindow.chaynsLoggerConfigs;
    if (applicationUid !== null) {
        const applicationIndex = _configs.findIndex((conf) => conf.applicationUid === applicationUid);
        if (applicationIndex > -1) {
            _configs[applicationIndex][key] = value;
            return;
        }
    }
    const findConfigIndex = _configs.findIndex((conf) => conf.applicationUid === currentApplicationUid);
    const index = findConfigIndex > - 1 ? findConfigIndex : 0;
    _configs[index][key] = value;
}
